import * as classNames from 'classnames';
import * as React from 'react';
import {Room} from 'twilio-video';
import {CollabMode, InterviewType} from '../../common/interview-type';

import {ISessionChallenge} from '../../redux/modules/session/reducer';

import {
    WS_SEND_ACK,
    WS_SEND_END_INTERVIEW,
    WS_SEND_LANGUAGE,
    WS_SEND_RUN,
    WS_SEND_SWAP_ROLE,
} from '../../redux/modules/web-socket/constants';
import {WHITE_LABEL} from '../../utils/config';
import {isMobile} from '../../utils/helpers';
import {track} from '../../utils/tracker';

import {AlertModal} from '../alert-modal/alert-modal';
import {SessionEditor} from '../session-editor/session-editor';

import {WebSocketClient} from '../session-router/web-socket-client';
import {ConnectionlessVideo} from '../video-pane/connectionless-video';
import {VideoContainer} from '../video-pane/video-pane';
import {CollabSidebar} from './collab-sidebar';
import {SessionDashboardButton as DashboardButton} from './dashboard-button/dashboard-button';

import {DEFAULT_TITLE, INTERVIEWER_TITLE, TRYOUT_TITLE, TUTORIAL_TITLE} from './data';
import {SessionEndInterview as EndInterview} from './end-interview/end-interview';
import {SessionLanguagePopover as LanguagePopover} from './language-popover/language-popover';
import {SessionLeftBar} from './left-bar/left-bar';
import {MobileTabs} from './mobile-tabs';
import {SessionQuestionPopover as QuestionPopover} from './question-popover/question-popover';
import {SessionRunTests as RunTests} from './run-tests/run-tests';

import {
    body            as cssBody,
    buttonGroup     as cssButtonGroup,
    header          as cssHeader,
    layout          as cssLayout,
    collab          as cssCollab,
    collabVideo     as cssCollabVideo,
    modalContent    as cssModalContent,
    link            as cssLink,
    hidden          as cssHidden,
} from './session.module.sass';
import {SessionSignupButton as SignupButton} from './signup-button/signup-button';
import {SessionSplitPane as SplitPane} from './split-pane/split-pane';
import {SessionSwapRolesButton as SwapRolesButton} from './swap-roles-button/swap-roles-button';
import {SessionTabs as Tabs} from './tabs/tabs';
import {SessionTitle as Title} from './title/title';
import {TutorialPane} from './tutorial-pane';
import {Tour, TutorialSteps} from './tutorial-steps';

import {SessionVideo} from './video/video';

export interface ISessionConnected extends ISessionChallenge {
    showRunCodeHintAcknowledged: boolean;
    showConsoleScrollHint: boolean;
    webSocketConnected: boolean;
    hadTestsUnlocked: boolean;
    showRunCodeHint: boolean;
    showVideoHint: boolean;
    leftBarHidden: boolean;
    interviewType: InterviewType;
    isInterviewer: boolean;
    dashboardUrl: string;
    initialCode: string;
    language: string;
    hasTests: boolean;
    running: boolean;
    link: string;
    startTime: Date;
    isSendingScreen: boolean;
    sendScreenError: string;
    room: Room;
}

export interface ISessionActions {
    toggleLeftBar?: (state?: boolean) => void;
    sendScreen: (room: Room, interviewType: InterviewType, startTime: Date) => any;
    confirmSendScreenError: () => any;
}

export interface ISessionProps extends ISessionConnected, ISessionActions {
    socket?: WebSocketClient;
    tryOutMode?: boolean;
    tutorialMode?: boolean;
    userId?: string;
}

interface ISessionState {
    collabMode: CollabMode;
    tour: Tour;
    webSocketConnectedAlertModalShown?: boolean;
    isTourLoaded: boolean;
}

const updateChromeUrl = 'https://support.google.com/chrome/answer/95414';
const updateFirefoxUrl = 'https://support.mozilla.org/en-US/kb/update-firefox-latest-version';

const getInitialTour = (props: ISessionProps) => {
    const {tutorialMode, isInterviewer} = props;
    const isMobileLayout = isMobile();

    if (tutorialMode) {
        return isMobileLayout ? Tour.Mobile : Tour.Desktop;
    }
    if (isMobileLayout) {
        return isInterviewer ? Tour.Interviewer : Tour.Interviewee;
    }
    return Tour.None;
};

export class SessionUi extends React.Component<ISessionProps, ISessionState> {
    public bodyRef = React.createRef<HTMLDivElement>();

    private lastCollabChange: number;

    public state: ISessionState = {
        collabMode: this.props.interviewType.defaultCollabMode,
        tour: Tour.None,
        webSocketConnectedAlertModalShown: false,
        isTourLoaded: false,
    };

    public componentDidMount(): void {
        this.loadTour();
    }

    public render(): React.ReactElement<any> {
        const {
            collabMode,
            tour,
            webSocketConnectedAlertModalShown,
        } = this.state;

        const {
            showConsoleScrollHint,
            webSocketConnected,
            hadTestsUnlocked,
            showRunCodeHint,
            showVideoHint,
            interviewType,
            isInterviewer,
            dashboardUrl,
            initialCode,
            tryOutMode,
            tutorialMode,
            challenge,
            language,
            hasTests,
            running,
            userId,
            link,
            toggleLeftBar,
            leftBarHidden,
            isSendingScreen,
            sendScreenError,
            room,
            confirmSendScreenError,
        } = this.props;

        const shouldShowContent = isInterviewer || interviewType.showQuestionToInterviewee;
        const isMobileLayout = isMobile();
        const hasMobileContent = shouldShowContent && isMobileLayout;

        const canToggleLeftBar = (
            interviewType.canSwitchCollabMode
            || collabMode === CollabMode.Code
        );
        const connectionless = tryOutMode || tutorialMode;

        const collab = (
            <div className={classNames(cssCollab, {
                [cssHidden]: hasMobileContent && !leftBarHidden,
            })}>
                {interviewType.canSwitchCollabMode &&
                    <CollabSidebar
                        leftBarHidden={leftBarHidden}
                        toggleLeftBar={this.toggleLeftBarFromCollabSidebar}
                        collabMode={collabMode}
                        setCollabMode={this.setCollabMode}
                    />
                }
                <SessionEditor
                    hidden={collabMode !== CollabMode.Code}
                    connectionless={connectionless}
                    initialCode={initialCode}
                    connected={webSocketConnected}
                    language={language}
                    disabled={running}
                    link={link}
                    userId={userId}
                    selectLanguage={this.changeLanguage}
                    languagePopover={<LanguagePopover ackLanguage={this.ackLanguage} />}
                    showConsoleScrollHint={showConsoleScrollHint}
                    ackConsoleScroll={this.ackConsoleScroll}
                    runTests={hasTests &&
                        <RunTests
                            hadTestsUnlocked={hadTestsUnlocked}
                            isInterviewer={isInterviewer}
                            onClick={this.runTests}
                        />
                    }
                    runCode={this.runCode}
                    showRunCodeHint={showRunCodeHint}
                />
                {collabMode === CollabMode.Video &&
                    <div className={cssCollabVideo}>
                        {tryOutMode ? <ConnectionlessVideo />
                            : tutorialMode ? (
                                <TutorialPane
                                    interviewType={interviewType}
                                    startTour={this.startTour}
                                />
                            )
                            : (
                                <VideoContainer
                                    isSendingScreen={isSendingScreen}
                                    room={room}
                                    runCommand={this.runCommand}
                                    sendScreen={this.sendScreen}
                                />
                            )
                        }
                    </div>
                }
                {collabMode === CollabMode.Screen &&
                    <div className={cssCollabVideo}>
                        {connectionless
                            ? <ConnectionlessVideo isShowingPeerScreen />
                            : (
                                <VideoContainer
                                    isSendingScreen={isSendingScreen}
                                    room={room}
                                    runCommand={this.runCommand}
                                    sendScreen={this.sendScreen}
                                    isShowingPeerScreen
                                />
                            )
                        }
                    </div>
                }
            </div>
        );

        const draggableVideo = (collabMode === CollabMode.Code &&
            <SessionVideo
                ackVideo={this.ackVideo}
                runCommand={this.runCommand}
                connectionless={connectionless}
                showVideoHint={showVideoHint}
                getContentBoundingClientRect={this.getContentBoundingClientRect}
                isSendingScreen={isSendingScreen}
                room={room}
                sendScreen={this.sendScreen}
            />
        );

        const contentPane = (
            <SessionLeftBar
                hidden={hasMobileContent && leftBarHidden}
                toggleLeftBar={canToggleLeftBar ? toggleLeftBar : undefined}
            >
                <Tabs challenge={challenge} />
                <QuestionPopover ackQuestion={this.ackQuestion} />
            </SessionLeftBar>
        );

        return (
            <div className={cssLayout}>
                <div className={cssHeader}>
                    <div className={cssButtonGroup}>
                        {tryOutMode ? <SignupButton endInterview={this.endInterview} />
                            : tutorialMode || isMobileLayout ? undefined
                            : (
                                <DashboardButton
                                    url={dashboardUrl}
                                    target={WHITE_LABEL ? '_self' : '_blank'}
                                />
                            )
                        }

                      {interviewType.swapRoles ? <SwapRolesButton swapRoles={this.swapRoles} /> : <div/>}
                    </div>

                    <Title>
                        {tryOutMode ? TRYOUT_TITLE
                            : tutorialMode ? TUTORIAL_TITLE
                            : this.getSessionTitle()
                        }
                    </Title>

                    <EndInterview
                        endInterview={this.endInterview}
                        swapRoles={this.swapRoles}
                        interviewTypeSwapRoles={interviewType.swapRoles}
                        skipConfirm={tutorialMode}
                    />
                </div>

                {hasMobileContent
                    ?
                        <>
                            <MobileTabs
                                shouldShowContent={!leftBarHidden}
                                toggleContent={() => toggleLeftBar()}
                            />
                            {contentPane}
                            {collab}
                        </>

                    : shouldShowContent ?
                        <div className={cssBody} ref={this.bodyRef}>
                            <SplitPane>
                                {contentPane}
                                {collab}
                                {draggableVideo}
                            </SplitPane>
                        </div>

                    :
                        <div className={cssBody} ref={this.bodyRef}>
                            {collab}
                            {draggableVideo}
                        </div>
                }

                <AlertModal
                    isOpen={webSocketConnectedAlertModalShown}
                    onClose={this.hideWebSocketConnectedAlertModal}
                >
                  <p>The server seems to be unavailable, please try again in a few seconds</p>
                </AlertModal>

                <AlertModal
                    isOpen={!!sendScreenError}
                    onClose={confirmSendScreenError}
                    title="We can't access your screen"
                >
                    <p className={cssModalContent}>
                        Make sure you're using a browser that supports screen sharing,
                        such as the latest version of Chrome or Firefox.
                    </p>
                    <p className={cssModalContent}>
                        <a className={cssLink} href={updateChromeUrl} target='_blank'>
                            How to update Chrome
                        </a>
                    </p>
                    <p className={cssModalContent}>
                        <a className={cssLink} href={updateFirefoxUrl} target='_blank'>
                            How to update Firefox
                        </a>
                    </p>
                </AlertModal>

                {tour !== Tour.None &&
                    <TutorialSteps
                        onExit={this.endTour}
                        toggleContent={toggleLeftBar}
                        tour={tour}
                    />
                }
            </div>
        );
    }

    public componentDidUpdate(prev: ISessionProps): void {
        const {isInterviewer, toggleLeftBar} = this.props;
        const isMobileLayout = isMobile();

        // open left bar after swapping role
        if (prev.isInterviewer !== isInterviewer) {
            toggleLeftBar(isMobileLayout);

            if (isMobileLayout) {
                this.setState({tour: isInterviewer ? Tour.Interviewer : Tour.Interviewee});
            }
        }
    }

    private loadTour = (): void => {
        if (!this.state.isTourLoaded) {
            this.setState({
                tour: getInitialTour(this.props),
                isTourLoaded: true,
            });
        }
    };

    private getSessionTitle = (): string => {
        return this.props.isInterviewer ? INTERVIEWER_TITLE : DEFAULT_TITLE;
    };

    private ifConnected = (action: () => void): void => {
        return this.props.webSocketConnected ? action() : this.showWebSocketConnectedAlertModal();
    };

    private runCommand = (command: string, data: any = {}): void => {
        this.props.socket.send(command, data);
    };

    private runCode = (code: string, runTests: boolean = false): void => {
        const {language, showRunCodeHintAcknowledged} = this.props;

        this.ifConnected(() => {
            showRunCodeHintAcknowledged || this.runCommand(WS_SEND_ACK, 'showRunCodeHint');
            this.runCommand(WS_SEND_RUN, {code, language, runTests});
            track('session run code', {runWithTests: runTests});
        });
    };

    private runTests = (code: string): void => {
        this.runCode(code, true);
        this.ackConfirmRunningTests();
    };

    private changeLanguage = (language: string): void => {
        this.ifConnected(() => this.runCommand(WS_SEND_LANGUAGE, {language}));
    };

    private swapRoles = (): void => {
        if (this.props.tutorialMode) {
            this.setState({tour: Tour.SwapRoles});
            return;
        }
        this.ifConnected(() => this.runCommand(WS_SEND_SWAP_ROLE));
    };

    private endInterview = (): void => {
        if (this.props.tutorialMode) {
            this.setState({tour: Tour.EndSession});
            return;
        }
        this.ifConnected(() => this.runCommand(WS_SEND_END_INTERVIEW));
    };

    private ackLanguage = (): void => {
        this.ifConnected(() => this.runCommand(WS_SEND_ACK, 'language'));
    };

    private ackQuestion = (): void => {
        this.ifConnected(() => this.runCommand(WS_SEND_ACK, 'question'));
    };

    private ackVideo = (): void => {
        this.ifConnected(() => this.runCommand(WS_SEND_ACK, 'showVideoHint'));
    };

    private ackConfirmRunningTests = (): void => {
        this.ifConnected(() => this.runCommand(WS_SEND_ACK, 'unlockTests'));
    };

    private ackConsoleScroll = (): void => {
        this.ifConnected(() => {
            this.runCommand(WS_SEND_ACK, 'showConsoleScrollHint');
            track('session show console hint');
        });
    };

    private getContentBoundingClientRect = (): ClientRect => (
        this.bodyRef.current.getBoundingClientRect()
    );

    private showWebSocketConnectedAlertModal = () => {
        this.setState({webSocketConnectedAlertModalShown: true});
    };

    private hideWebSocketConnectedAlertModal = () => {
        this.setState({webSocketConnectedAlertModalShown: false});
    };

    private setCollabMode = collabMode => {
        this.trackCollabSidebarClick(collabMode);
        this.setState({collabMode});
    };

    private toggleLeftBarFromCollabSidebar = state => {
        this.trackCollabSidebarClick('content');
        this.props.toggleLeftBar(state);
    };

    private trackCollabSidebarClick = selection => {
        const {
            lastCollabChange,
            props: {
                interviewType: {apiName: interviewType},
                isInterviewer,
                leftBarHidden,
                startTime,
            },
            state: {collabMode},
        } = this;

        const now = Date.now();
        this.lastCollabChange = now;

        track('click collab sidebar', {
            collabMode,
            content: leftBarHidden ? 'hidden' : 'shown',
            interviewType,
            minutesFromStart: Math.floor((now - startTime.getTime()) / (1000 * 60)),
            role: isInterviewer ? 'interviewer' : 'interviewee',
            secondsFromLastChange: lastCollabChange
                ? Math.floor((now - lastCollabChange) / 1000)
                : undefined,
            selection,
        });
    };

    private sendScreen = () => {
        const {props: {interviewType, room, sendScreen, startTime}} = this;
        sendScreen(room, interviewType, startTime);
    };

    private startTour = () => this.setState({tour: isMobile() ? Tour.Mobile : Tour.Desktop});
    private endTour = () => this.setState({tour: Tour.None});
}
